import { Link } from '@tanstack/react-router';

import { NotFoundState } from '@/shared/components/NotFoundState';

export interface LinkedRunEmptyStateProps {
  workspaceId: string;
  workflowKey: string;
  taskId: string;
}

/**
 * A stale or inapplicable run-page link (spec.md US3, FR-006): the parent
 * task is not currently on an open `linked_workflow` join. Covers both "never
 * was one" and "moved on since" — `resolveRun`'s `not-linked` branch does not
 * distinguish the two causes, so neither does this state.
 *
 * Built during T010 (US1), ahead of its own task (T019, US3): `LinkedRunPage`
 * needed something real to render on `not-linked` as soon as its route
 * existed, since `resolveRun`'s not-linked branch was already reachable (a
 * task whose join closes while the run page is open) — a placeholder here
 * would have been a broken page, not merely an incomplete one. Covered by its
 * own test in `LinkedRunEmptyState.test.tsx` (T017) and end-to-end through
 * the full route in `tests/routes/linked-run.test.tsx` (T018).
 */
export function LinkedRunEmptyState({
  workspaceId,
  workflowKey,
  taskId,
}: LinkedRunEmptyStateProps) {
  return (
    // A finished run is shown, read-only, rather than landing here — so this
    // state now means there is no run to show at all.
    <NotFoundState
      title="No sub-workflow run to show"
      description="This task has not run a sub-workflow step, or the link names a run it does not have."
      action={
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/board"
          params={{ workspaceId, workflowKey }}
          search={{ task: taskId, tab: 'relations' as const }}
          className="text-foreground rounded-sm text-sm underline underline-offset-4"
        >
          Back to the task
        </Link>
      }
    />
  );
}
